/**
 * Image utility functions for handling S3 direct URLs and fallbacks
 */

export const getApiBaseUrl = (): string =>
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3001";

/**
 * Convert S3 key to direct S3 URL
 */
export const getS3Url = (s3Key: string | undefined | null): string => {
  if (!s3Key) return '';
  
  const s3BaseUrl = 'https://podscope.s3.amazonaws.com';
  
  // Remove leading slash if present
  const cleanKey = s3Key.startsWith('/') ? s3Key.slice(1) : s3Key;
  
  return `${s3BaseUrl}/${cleanKey}`;
};

/** Server-side accent color for an S3 image (JSON). */
export const getProxiedS3AccentColorUrl = (
  s3Key: string | undefined | null,
): string => {
  if (!s3Key) return "";
  const cleanKey = s3Key.startsWith("/") ? s3Key.slice(1) : s3Key;
  return `${getApiBaseUrl()}/api/proxy/s3-accent-color/${cleanKey}`;
};

/**
 * Get the best thumbnail URL for any content type
 * Only uses S3 direct URLs when S3 keys are actually stored, otherwise uses original URLs
 */
export const getContentThumbnail = (content: any): string | undefined => {
  return getS3Url(content.imageS3Key);
};

/**
 * Preload image with error handling
 */
export const preloadImage = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Get image with fallback chain
 */
export const getImageWithFallback = async (
  primaryUrl?: string, 
  fallbackUrl?: string
): Promise<string | undefined> => {
  if (primaryUrl) {
    const loaded = await preloadImage(primaryUrl);
    if (loaded) return primaryUrl;
  }
  
  if (fallbackUrl) {
    const loaded = await preloadImage(fallbackUrl);
    if (loaded) return fallbackUrl;
  }
  
  return undefined;
};

/**
 * Add cache-busting parameter to URL to force image reload
 */
export const addCacheBuster = (url: string): string => {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${Date.now()}`;
};

/**
 * Program/podcast cover URL. Appends ?v= only when imageUpdatedAt is set (on S3 re-upload),
 * so the URL stays stable and cacheable until the image actually changes.
 */
export const getProgramImageUrl = (program: {
  imageS3Key?: string | null;
  imageUpdatedAt?: string | Date | null;
}): string => {
  const url = ensureDirectS3Url(getS3Url(program.imageS3Key || ''));
  if (!url) return '';
  if (program.imageUpdatedAt == null) return url;
  const version = new Date(program.imageUpdatedAt).getTime();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${version}`;
};

/** Podcast/channel cover: S3 key + cache-bust, else external imageUrl (e.g. YouTube). */
export const getPodcastImageUrl = (podcast: {
  imageS3Key?: string | null;
  imageUrl?: string | null;
  imageUpdatedAt?: string | Date | null;
}): string => {
  if (podcast.imageS3Key) {
    const url = getProgramImageUrl({
      imageS3Key: podcast.imageS3Key,
      imageUpdatedAt: podcast.imageUpdatedAt,
    });
    if (!url) return '';
    // When imageUpdatedAt is set (after S3 upload), URL is already versioned like programs.
    // Otherwise bust on every load so re-uploaded S3 keys aren't stuck in browser cache.
    if (podcast.imageUpdatedAt == null) return addCacheBuster(url);
    return url;
  }
  if (podcast.imageUrl) return addCacheBuster(podcast.imageUrl);
  return '';
};

/**
 * Podcast/channel cover (S3 key, else external imageUrl) without a per-call cache buster,
 * so avatars rendered on every re-render keep a stable src.
 */
export const getChannelFallbackImageUrl = (
  podcast:
    | {
        imageS3Key?: string | null;
        imageUrl?: string | null;
      }
    | null
    | undefined,
): string => {
  const key = podcast?.imageS3Key?.trim();
  if (key) return getProgramImageUrl({ imageS3Key: key });
  return podcast?.imageUrl?.trim() ?? '';
};

/** Program cover with podcast/channel fallback when the program has no image. */
export const resolveProgramCoverUrl = (args: {
  program: {
    imageS3Key?: string | null;
    imageUpdatedAt?: string | Date | null;
  };
  podcast?: {
    imageS3Key?: string | null;
    imageUrl?: string | null;
    imageUpdatedAt?: string | Date | null;
  } | null;
}): string | undefined => {
  if (args.program.imageS3Key) {
    const url = getProgramImageUrl(args.program);
    return url || undefined;
  }
  if (args.podcast) {
    const url = getPodcastImageUrl(args.podcast);
    return url || undefined;
  }
  return undefined;
};

/**
 * Ensure URL is a direct S3 URL instead of proxied URL
 */
export const ensureDirectS3Url = (url: string): string => {
  if (!url) return url;
  // If it's already a direct S3 URL, return as is
  if (url.includes('podscope.s3.amazonaws.com')) {
    return url;
  }
  // If it's a proxy URL, convert to direct S3 URL
  if (url.includes('/api/proxy/s3/')) {
    const key = url.split('/api/proxy/s3/')[1];
    return `https://podscope.s3.amazonaws.com/${key}`;
  }
  // If it's a relative path, make it absolute
  if (url.startsWith('/')) {
    return `https://podscope.s3.amazonaws.com${url}`;
  }
  return url;
};
